import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Studio.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="st-modal-overlay" onClick={onClose}>
      <div className="st-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="st-modal__image" />
        <span className="st-modal__label">{label}</span>
      </div>
    </div>
  );
}

const MODELS = [
  { id: "nano-banana", label: "Google Nano Banana Pro" },
  { id: "flux2", label: "Flux2.Dev" },
];

const RATIOS = ["1:1", "3:4", "4:3"];
const RESOLUTIONS = ["768", "1024"];

export default function StudioCharacterSheet() {
  /* --- Characters --- */
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState("");

  /* --- Wardrobe --- */
  const [wardrobe, setWardrobe] = useState({ garments: [], accessories: [] });
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);

  /* --- Saved sheets --- */
  const [savedSheets, setSavedSheets] = useState([]);

  /* --- Prompts --- */
  const [idea, setIdea] = useState("");
  const [prompter, setPrompter] = useState("");

  /* --- Settings --- */
  const [model, setModel] = useState("nano-banana");
  const [ratio, setRatio] = useState("1:1");
  const [resolution, setResolution] = useState("1024");
  const [seed, setSeed] = useState("");

  /* --- Generation --- */
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);

  /* --- Reel refs --- */
  const charsReelRef = useRef(null);
  const garmentsReelRef = useRef(null);
  const accessoriesReelRef = useRef(null);
  const sheetsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* --- Load data --- */
  useEffect(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    fetch(`${API_URL}/characters`, { headers })
      .then((r) => r.json())
      .then(async (d) => {
        const chars = d.characters || [];
        const detailed = await Promise.all(
          chars.map(async (c) => {
            const cid = c.id || c;
            try {
              const r2 = await fetch(`${API_URL}/characters/${cid}`, { headers });
              const det = await r2.json();
              return { id: cid, displayName: det.display_name || cid, faceUrl: det?.face?.reference?.[0]?.url || null };
            } catch { return { id: cid, displayName: cid, faceUrl: null }; }
          })
        );
        setCharacters(detailed);
      })
      .catch(() => {});
    fetch(`${API_URL}/wardrobe`, { headers })
      .then((r) => r.json())
      .then((d) => setWardrobe({ garments: d.garments || [], accessories: d.accessories || [] }))
      .catch(() => {});
  }, []);

  /* --- Load sheets when char changes --- */
  useEffect(() => {
    if (!selectedChar) { setSavedSheets([]); return; }
    fetch(`${API_URL}/characters/${selectedChar}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.json())
      .then((d) => setSavedSheets(d.sheets || []))
      .catch(() => setSavedSheets([]));
  }, [selectedChar]);

  const toggleGarment = (url) => setSelectedGarments((p) => p.includes(url) ? p.filter((u) => u !== url) : [...p, url]);
  const toggleAccessory = (url) => setSelectedAccessories((p) => p.includes(url) ? p.filter((u) => u !== url) : [...p, url]);

  const pollRef = useRef(false);
  const pollJob = async (jobId) => {
    pollRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/studio/character-sheet/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const rr = await fetch(`${API_URL}/studio/character-sheet/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const rd = await rr.json();
          if (rd.image) setResult(rd.image);
          setStatus("done");
          pollRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollRef.current = false;
          return;
        }
      } catch (err) { console.error("Poll error:", err); }
    }
    setErrorMsg("Tiempo agotado"); setStatus("error"); pollRef.current = false;
  };

  const canGenerate = selectedChar && (selectedGarments.length + selectedAccessories.length) >= 1 && status !== "generating";

  const handleGenerate = async () => {
    setStatus("generating");
    setResult(null);
    setErrorMsg("");
    try {
      const body = {
        character_id: selectedChar,
        garment_urls: selectedGarments,
        accessory_urls: selectedAccessories,
        idea, prompter, model, ratio, resolution, seed,
      };
      const res = await fetch(`${API_URL}/studio/character-sheet/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollJob(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al generar");
      setStatus("error");
    }
  };

  const getResultSrc = () => {
    if (!result) return null;
    return result.startsWith("data:") ? result : `data:image/png;base64,${result}`;
  };

  return (
    <div className="studio-page">
      {/* LEFT */}
      <div className="st-panel st-panel--left">
        <div className="st-header">
          <h2 className="st-panel__title">Crear Hoja del Personaje</h2>
          <button
            className={`st-btn-primary ${status === "generating" ? "st-btn-primary--loading" : ""}`}
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {status === "generating" ? "Generando..." : "Generar"}
          </button>
        </div>

        {/* Character reel */}
        <section className="st-section">
          <span className="st-section__title">Personaje base</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(charsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={charsReelRef}>
              {characters.map((c) => (
                <div
                  key={c.id}
                  className={`st-reel-item ${selectedChar === c.id ? "st-reel-item--selected" : ""}`}
                  onClick={() => { setSelectedChar(c.id); setSelectedGarments([]); setSelectedAccessories([]); setResult(null); setStatus("idle"); }}
                >
                  {c.faceUrl ? <img src={c.faceUrl} alt={c.displayName} /> : <div className="st-reel-item__placeholder" />}
                  <span className="st-reel-item__name">{c.displayName}</span>
                </div>
              ))}
              {characters.length === 0 && <span className="st-reel-empty">Crea personajes en Crear Personaje Base</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(charsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Garments reel */}
        <section className="st-section">
          <span className="st-section__title">Ropa</span>
          <div className="st-reel st-reel--tall">
            <button className="st-reel__btn" onClick={() => scrollReel(garmentsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={garmentsReelRef}>
              {wardrobe.garments.map((g) => (
                <div
                  key={g.url}
                  className={`st-reel-item st-reel-item--wide ${selectedGarments.includes(g.url) ? "st-reel-item--selected" : ""}`}
                  onClick={() => toggleGarment(g.url)}
                >
                  <img src={g.url} alt={g.name} />
                  <span className="st-reel-item__name">{g.name?.replace(".png", "").replace(/_/g, " ")}</span>
                </div>
              ))}
              {wardrobe.garments.length === 0 && <span className="st-reel-empty">Crea ropa en Crear Detalles</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(garmentsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Accessories reel */}
        <section className="st-section">
          <span className="st-section__title">Complementos</span>
          <div className="st-reel st-reel--tall">
            <button className="st-reel__btn" onClick={() => scrollReel(accessoriesReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={accessoriesReelRef}>
              {wardrobe.accessories.map((a) => (
                <div
                  key={a.url}
                  className={`st-reel-item st-reel-item--wide ${selectedAccessories.includes(a.url) ? "st-reel-item--selected" : ""}`}
                  onClick={() => toggleAccessory(a.url)}
                >
                  <img src={a.url} alt={a.name} />
                  <span className="st-reel-item__name">{a.name?.replace(".png", "").replace(/_/g, " ")}</span>
                </div>
              ))}
              {wardrobe.accessories.length === 0 && <span className="st-reel-empty">Sin complementos</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(accessoriesReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Prompts */}
        <section className="st-section">
          <span className="st-section__title">Idea</span>
          <textarea
            className="st-textarea"
            placeholder="Matices para esta hoja... (ej: mojado por la lluvia, expresion seria)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
        </section>
        <section className="st-section">
          <span className="st-section__title">Prompter</span>
          <textarea className="st-textarea" placeholder="Prompt tecnico..." value={prompter} onChange={(e) => setPrompter(e.target.value)} />
        </section>

        {/* Saved sheets */}
        {selectedChar && (
          <section className="st-section">
            <span className="st-section__title">Hojas guardadas</span>
            <div className="st-reel">
              <button className="st-reel__btn" onClick={() => scrollReel(sheetsReelRef, -1)}>{"\u25C0"}</button>
              <div className="st-reel__frames" ref={sheetsReelRef}>
                {savedSheets.length > 0 ? (
                  savedSheets.map((s) => (
                    <div key={s.url} className="st-reel-item st-reel-item--sheet" onClick={() => setModalSrc(s.url)}>
                      <img src={s.url} alt={s.name} />
                      <span className="st-reel-item__name">{s.name?.replace(".png", "").replace(/_/g, " ")}</span>
                    </div>
                  ))
                ) : (
                  <span className="st-reel-empty">Genera y guarda hojas</span>
                )}
              </div>
              <button className="st-reel__btn" onClick={() => scrollReel(sheetsReelRef, 1)}>{"\u25B6"}</button>
            </div>
          </section>
        )}

        {/* Bottom bar */}
        <div className="st-bottom-bar">
          <div className="st-select-group">
            <span className="st-select-group__label">Motor</span>
            <select className="st-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Ratio</span>
            <select className="st-select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Resolucion</span>
            <select className="st-select" value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}px</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Semilla</span>
            <input className="st-seed-input" placeholder="random" value={seed} onChange={(e) => setSeed(e.target.value)} />
          </div>
          <span className="st-credits">Creditos est.: --</span>
        </div>

        {errorMsg && <div className="st-error">{errorMsg}</div>}
      </div>

      {/* RIGHT */}
      <div className="st-panel st-panel--right">
        <h2 className="st-panel__title">Resultado</h2>
        <div className="st-result">
          <div className={`st-result__image ${result ? "st-result__image--loaded" : ""}`}>
            {result ? (
              <img src={getResultSrc()} alt="Hoja" />
            ) : (
              <div className="st-results-empty">
                {status === "generating" ? <span className="st-spinner" /> : <p>Selecciona personaje + prendas y pulsa Generar</p>}
              </div>
            )}
          </div>
          {result && (
            <div className="st-result__footer">
              <span className="st-result__label">Hoja de Personaje</span>
              <div className="st-result__actions">
                <button className="st-btn-secondary">Guardar</button>
                <button className="st-btn-download">&#x2B73;</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Hoja" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
